/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import {useCallback, useMemo} from 'react';
import {useInfiniteQuery} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {getAssetHistory} from '@ion/core/src/services/ArcGIS';
import {AssetQueryParams} from '@ion/core/src/types/services';
import {createAssetsMap} from '@ion/dls/src/maps/helpers/MapViewUtils';
import {createKey, QueryKey} from '../config/QueryKeys';
import {
  createQueryResult,
  DurationResult,
  filterPagesWithAssets,
  getAlignedRoutes,
  getStaleTime
} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseLoadHistoryAssetResults = UseDefaultQueryResult & {
  results: object[];
  refresh: Function;
  hasNextPage: boolean;
};

export interface UseLoadHistoryAssets {
  baseKey: QueryKey;
  enabled?: boolean;
  isTracking2?: boolean;
  enableAutoNextPageFetch?: boolean;
  queryParams?: AssetQueryParams;
  staleTime?: number | DurationResult;
  batchSize?: number;
}

/**
 * Custom React hook to load and paginate historical asset data with  with automatic route matching and 
 * optional automatic fetching of next pages.
 * 
 * Utilizes React Query's `useInfiniteQuery` to fetch asset history in batches,
 * supports route matching for assets when applicable, and provides stale time control.
 * 
 * ```typescript
 *
 * import {useLoadHistoryAssetsV2} from '@ion/query';
 *
 * ...
 *
 * const {historyAssetsUpdates} = useLoadHistoryAssetsV2(params);
 *
 * ```
 * 
 * @param {UseLoadHistoryAssets} params - Configuration object:
 *   - baseKey: Query key used for caching and invalidation.
 *   - queryParams: Parameters for asset history query including assets, start/end times.
 *   - batchSize: Number of assets to fetch per page (default 1).
 *   - enabled: Flag to enable or disable the query (default true).
 *   - isTracking2: Toggle tracking mode for fetching (default true).
 *   - enableAutoNextPageFetch: Automatically fetch next page if available (default true).
 *   - staleTime: Time in milliseconds before cached data becomes stale.
 * 
 * @returns {UseLoadHistoryAssetResults} - Returns query data with:
 *   - results: Array of asset history data combined with route information.
 *   - refresh: Function to manually refresh the query data.
 *   - hasNextPage: Boolean indicating if more pages are available.
 */
export const useLoadHistoryAssetsV2 = ({
  baseKey,
  queryParams,
  batchSize = 1,
  enabled = true,
  isTracking2 = true,
  enableAutoNextPageFetch = true,
  staleTime = getStaleTime(60, false, false)
}: UseLoadHistoryAssets): UseLoadHistoryAssetResults => {
  const keys = useMemo(() => (
    createKey(baseKey, queryParams)
  ), [baseKey, queryParams]);

  const assetList = useMemo(() => {
    return queryParams?.params?.assets || [];
  }, [queryParams]);

  const fetchAssets = useCallback(async({pageParam = 0}) => {
    const start        = queryParams?.params?.start;
    const end          = queryParams?.params?.end;
    const geometry     = queryParams?.params?.geometry;
    const groups       = queryParams?.params?.groups;
    const startIndex   = pageParam * batchSize;
    const endIndex     = startIndex + batchSize;
    const currentBatch = assetList.slice(startIndex, endIndex);

    const params = {
      assets : currentBatch,
      geometry,
      groups,
      start,
      end
    };
    
    try {
      const assets    = await getAssetHistory(params, isTracking2);
      let assetRoutes = {};
      if (assets?.length) {
        const assetsMap = createAssetsMap(assets);
        assetRoutes     = await getAlignedRoutes(assetsMap, {});
      }
      return {
        assets,
        assetRoutes,
        nextPage : pageParam + 1
      };
    } catch (err) {
      error(err);
      return {
        assets      : [],
        assetRoutes : {},
        nextPage    : null
      };
    }
  }, [queryParams, assetList, batchSize, isTracking2]);

  const query = useInfiniteQuery({
    queryKey         : keys,
    queryFn          : fetchAssets,
    getNextPageParam : (lastPage, allPages) => {
      const totalAssets  = assetList.length;
      const fetchedCount = allPages.length * batchSize;
      return fetchedCount < totalAssets ? lastPage.nextPage : undefined;
    },
    staleTime : staleTime as number,
    enabled
  } as any);

  const {
    data,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = query;

  if (enabled && enableAutoNextPageFetch && hasNextPage && !isFetchingNextPage) {
    fetchNextPage();
  }

  const pagesWithAssets = useMemo(() => filterPagesWithAssets(data), [data]);

  return createQueryResult(query, {
    results : pagesWithAssets || [],
    refresh : refetch,
    hasNextPage
  }) as UseLoadHistoryAssetResults;
};
